import React, { useState } from "react";

interface ScoringKeypadProps {
  canScore: boolean;
  tossSet: boolean;
  isIndoor: boolean;
  pendingRuns: number | null;
  pendingExtraType: "wide" | "noball" | "bye" | "legbye" | null;
  pendingWallBonus: boolean;
  onRunClick: (runs: number) => void;
  onWallBonusClick: () => void;
  onWicketClick: () => void;
  onExtraClick: (type: "wide" | "noball" | "bye" | "legbye") => void;
}

const ScoringKeypad: React.FC<ScoringKeypadProps> = ({
  canScore,
  tossSet,
  isIndoor,
  pendingRuns,
  pendingExtraType,
  pendingWallBonus,
  onRunClick,
  onWallBonusClick,
  onWicketClick,
  onExtraClick,
}) => {
  const [tappedButton, setTappedButton] = useState<string | null>(null);

  const disabled = !tossSet || !canScore;

  const handleTap = (buttonId: string, callback: () => void) => {
    if (disabled) return;
    setTappedButton(buttonId);
    setTimeout(() => setTappedButton(null), 200);
    callback();
  };

  const isRunActive = (n: number) => pendingRuns === n;
  const isExtraActive = (t: string) => pendingExtraType === t;

  return (
    <div className="v219-keypad">
      {/* SCORE RUNS */}
      <div className="v219-keypad-section">
        <div className="v219-keypad-label">Score Runs</div>
        <div className="v219-runs-grid">
          <RunButton label="0" tone="dot" active={isRunActive(0)} tapped={tappedButton === "run-0"} disabled={disabled} onClick={() => handleTap("run-0", () => onRunClick(0))} />
          <RunButton label="1" tone="run" active={isRunActive(1)} tapped={tappedButton === "run-1"} disabled={disabled} onClick={() => handleTap("run-1", () => onRunClick(1))} />
          <RunButton label="2" tone="run" active={isRunActive(2)} tapped={tappedButton === "run-2"} disabled={disabled} onClick={() => handleTap("run-2", () => onRunClick(2))} />
          <RunButton label="3" tone="run" active={isRunActive(3)} tapped={tappedButton === "run-3"} disabled={disabled} onClick={() => handleTap("run-3", () => onRunClick(3))} />
          <RunButton label="4" tone="boundary" active={isRunActive(4)} tapped={tappedButton === "run-4"} disabled={disabled} onClick={() => handleTap("run-4", () => onRunClick(4))} />
          <RunButton label="6" tone="boundary" active={isRunActive(6)} tapped={tappedButton === "run-6"} disabled={disabled} onClick={() => handleTap("run-6", () => onRunClick(6))} />
        </div>

        {isIndoor && (
          <button
            disabled={disabled}
            onClick={() => handleTap("wall", onWallBonusClick)}
            className={`v219-wall-btn ${pendingWallBonus ? "v219-wall-active" : ""} ${tappedButton === "wall" ? "sk-tapped" : ""}`}
          >
            W+1 • Wall Bonus
          </button>
        )}
      </div>

      {/* EXTRAS */}
      <div className="v219-keypad-section">
        <div className="v219-keypad-label">Extras</div>
        <div className="v219-extras-grid">
          <ExtraButton code="WD" label="Wide" active={isExtraActive("wide")} tapped={tappedButton === "extra-wide"} disabled={disabled} onClick={() => handleTap("extra-wide", () => onExtraClick("wide"))} />
          <ExtraButton code="NB" label="No Ball" active={isExtraActive("noball")} tapped={tappedButton === "extra-noball"} disabled={disabled} onClick={() => handleTap("extra-noball", () => onExtraClick("noball"))} />
          <ExtraButton code="BYE" label="Bye" active={isExtraActive("bye")} tapped={tappedButton === "extra-bye"} disabled={disabled} onClick={() => handleTap("extra-bye", () => onExtraClick("bye"))} />
        </div>
        <button
          disabled={disabled}
          onClick={() => handleTap("extra-legbye", () => onExtraClick("legbye"))}
          className={`v219-legbye-btn ${isExtraActive("legbye") ? "v219-legbye-active" : ""} ${tappedButton === "extra-legbye" ? "sk-tapped" : ""}`}
        >
          LB • Leg Bye
        </button>
      </div>

      {/* WICKET */}
      <div className="v219-keypad-section">
        <div className="v219-keypad-label">Wicket</div>
        <button
          disabled={disabled}
          onClick={() => handleTap("wicket", onWicketClick)}
          className={`v219-wicket-btn ${tappedButton === "wicket" ? "sk-tapped-wicket" : ""}`}
        >
          <span className="v219-wicket-letter">W</span>
          <span className="v219-wicket-sub">Wicket</span>
        </button>
      </div>

      <style>{`
        .v219-keypad {
          border-radius: 1rem;
          padding: 0.75rem 1rem 1.25rem;
          border: 1px solid rgba(16,185,129,0.2);
          background: linear-gradient(180deg, rgba(8,32,22,0.75) 0%, rgba(3,15,10,0.8) 100%);
          box-shadow: 0 8px 30px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04);
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .v219-keypad-section { display: flex; flex-direction: column; }
        .v219-keypad-label {
          color: #10b981;
          font-size: 0.625rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.16em;
          margin-bottom: 0.5rem;
        }
        .v219-runs-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
        }
        .v219-extras-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
        }
        .v219-wall-btn {
          margin-top: 0.5rem;
          width: 100%;
          border-radius: 0.75rem;
          padding: 0.625rem;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          border: 1px solid rgba(234,179,8,0.3);
          background: linear-gradient(135deg, rgba(180,120,20,0.7), rgba(140,90,15,0.7));
          color: #fef3c7;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .v219-wall-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, rgba(202,138,4,0.85), rgba(161,98,7,0.85));
        }
        .v219-wall-active {
          background: rgba(37,99,235,0.9) !important;
          border-color: rgba(96,165,250,0.5) !important;
          color: #fff;
          box-shadow: 0 0 0 3px rgba(96,165,250,0.25);
        }
        .v219-legbye-btn {
          margin-top: 0.5rem;
          width: 100%;
          border-radius: 0.75rem;
          padding: 0.5rem;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          border: 1px solid rgba(245,158,11,0.3);
          background: rgba(120,53,15,0.3);
          color: #fcd34d;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .v219-legbye-btn:hover:not(:disabled) { background: rgba(120,53,15,0.5); }
        .v219-legbye-active {
          background: rgba(37,99,235,0.9) !important;
          border-color: rgba(96,165,250,0.5) !important;
          color: #fff;
          box-shadow: 0 0 0 3px rgba(96,165,250,0.25);
        }
        .v219-wicket-btn {
          width: 100%;
          border-radius: 0.75rem;
          padding: 0.875rem;
          border: 1px solid rgba(239,68,68,0.3);
          background: linear-gradient(180deg, #b91c1c 0%, #7f1d1d 100%);
          box-shadow: 0 6px 20px rgba(185,28,28,0.45), inset 0 1px 0 rgba(255,255,255,0.15);
          color: #fff;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.125rem;
        }
        .v219-wicket-letter {
          font-size: 1.5rem;
          font-weight: 900;
          letter-spacing: 0.04em;
          line-height: 1;
        }
        .v219-wicket-sub {
          font-size: 0.5625rem;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: rgba(255,255,255,0.8);
        }
        @keyframes sk-press {
          0% { transform: scale(1); filter: brightness(1); }
          30% { transform: scale(0.94); filter: brightness(1.35); }
          100% { transform: scale(1); filter: brightness(1); }
        }
        @keyframes sk-press-wkt {
          0% { transform: scale(1); }
          30% { transform: scale(0.95); box-shadow: 0 0 32px rgba(239,68,68,0.6); }
          100% { transform: scale(1); }
        }
        .sk-tapped { animation: sk-press 0.22s cubic-bezier(0.25,0.46,0.45,0.94) forwards; }
        .sk-tapped-wicket { animation: sk-press-wkt 0.26s cubic-bezier(0.25,0.46,0.45,0.94) forwards; }
        .sk-run-btn { -webkit-tap-highlight-color: transparent; }
        .sk-run-btn:active:not(:disabled) { transform: scale(0.96); }
        .v219-keypad button:disabled { opacity: 0.4; cursor: not-allowed; filter: grayscale(0.3); }
      `}</style>
    </div>
  );
};

function RunButton({
  label,
  tone,
  active,
  tapped,
  disabled,
  onClick,
}: {
  label: string;
  tone: "dot" | "run" | "boundary";
  active: boolean;
  tapped: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const styles =
    active
      ? { bg: "linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)", border: "rgba(96,165,250,0.6)", text: "#fff" }
      : tone === "dot"
      ? { bg: "linear-gradient(180deg, #1a2620 0%, #0b1512 100%)", border: "rgba(255,255,255,0.08)", text: "#fff" }
      : tone === "boundary"
      ? { bg: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "rgba(74,222,128,0.35)", text: "#fff" }
      : { bg: "linear-gradient(180deg, #15803d 0%, #0f5a2a 100%)", border: "rgba(74,222,128,0.25)", text: "#fff" };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`sk-run-btn rounded-xl font-black text-4xl transition-all ${tapped ? "sk-tapped" : ""}`}
      style={{
        background: styles.bg,
        border: `1px solid ${styles.border}`,
        color: styles.text,
        minHeight: "3.75rem",
        boxShadow: active
          ? "0 0 0 3px rgba(96,165,250,0.25), 0 4px 14px rgba(37,99,235,0.35)"
          : "0 4px 14px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)",
      }}
    >
      {label}
    </button>
  );
}

function ExtraButton({
  code,
  label,
  active,
  tapped,
  disabled,
  onClick,
}: {
  code: string;
  label: string;
  active: boolean;
  tapped: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`sk-run-btn rounded-lg py-2.5 transition-all flex flex-col items-center justify-center gap-0.5 border ${
        active
          ? "bg-blue-600/90 border-blue-400/50 ring-2 ring-blue-400/50"
          : "bg-[#1a1208]/60 border-amber-500/30 hover:bg-[#241806]/70"
      } ${tapped ? "sk-tapped" : ""}`}
      style={{ minHeight: "3rem" }}
    >
      <span className={`text-base font-black ${active ? "text-white" : "text-amber-400"}`}>{code}</span>
      <span className={`text-[10px] ${active ? "text-white/90" : "text-amber-200/80"}`}>{label}</span>
    </button>
  );
}

export default ScoringKeypad;
